/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useId } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import IconArrowHead from '../../../../IconArrowHead/IconArrowHead';
import RangeFilterForm from './RangeFilterForm';
import { useHistory, useLocation } from 'react-router-dom';

import css from './RangeFilterPlain.module.css';
import useIsMobile from '../../../../../hooks/useIsMobile';
import { formatRange } from '../../../../../containers/SearchPage/SearchFilterPills/SearchFilterPills';

const RADIX = 10;
const DRAWER_VARIANT = 'drawer';

const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'range';

const parse = priceRange => {
  if (!priceRange) return null;

  const [minPrice, maxPrice] = priceRange.split(',').map(v => Number.parseFloat(v, RADIX));

  const result = {};

  if (minPrice != null && !Number.isNaN(minPrice)) {
    result.min = minPrice;
  }

  if (maxPrice != null && !Number.isNaN(maxPrice)) {
    result.max = maxPrice;
  }
  return Object.keys(result).length ? result : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { min: minPrice, max: maxPrice } = range || {};
  const value =
    minPrice && maxPrice
      ? `${minPrice},${maxPrice}`
      : minPrice
      ? `${minPrice},`
      : maxPrice
      ? `,${maxPrice}`
      : null;
  if (!value) return {};
  return { [queryParamName]: value };
};

const RangeFilterPlain = ({
  rootClassName,
  className,
  id,
  label,
  queryParamNames,
  initialValues,
  step,
  onSubmit,
  onBlur,
  unit,
  inputIcon,
  showTrigger = true,
  presets,
  variant,
  onToggle,
  showRangeInLabel = false,
  ...rest
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const isMobileDevice = useIsMobile();

  const reactId = useId();
  const drawerKey = id ?? `range-${reactId}`;

  const paramName = getQueryParamName(queryParamNames);
  const parsed = initialValues ? parse(initialValues[paramName]) : {};
  const { min: minVal, max: maxVal } = parsed || {};

  const hasValue = v => v != null;
  const hasInitialValues = initialValues && (hasValue(minVal) || hasValue(maxVal));

  const isFormVisible = showTrigger ? isOpen : true;

  const toggleIsOpen = () => {
    setIsOpen(prev => {
      const next = !prev;

      return next;
    });
    onToggle?.();
  };

  const close = () => {
    setIsOpen(false);
  };

  const buildCleanedQueryWithRange = (newRangeValueObj /* e.g. { [paramName]: '10,20' } */) => {
    const params = new URLSearchParams(location.search);
    const cleaned = {};

    for (const [key, value] of params.entries()) {
      if (key === paramName) continue;
      if (key === 'page') continue; // reset pagination on filter change
      cleaned[key] = value;
    }
    // merge current/updated range (if newRangeValueObj has a null value, it means remove)
    if (newRangeValueObj && Object.prototype.hasOwnProperty.call(newRangeValueObj, paramName)) {
      const val = newRangeValueObj[paramName];
      if (val != null) {
        cleaned[paramName] = val;
      } else {
        // explicit removal: ensure it's not present
        // nothing to do as we didn't add it
      }
    }

    return cleaned;
  };

  // Outside click
  useEffect(() => {
    if (!showTrigger) return;

    const handleClickOutside = e => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        close();
        onBlur?.();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, showTrigger]);

  const classes = classNames(
    rootClassName || css.root,
    {
      [css.rootOpen]: isOpen && showTrigger,
      [css.noTrigger]: !showTrigger,
      [css.drawerVariantRoot]: variant === DRAWER_VARIANT,
    },
    className
  );

  const handleChange = values => {
    if (showTrigger) {
      // setIsOpen(false);
    }
    const formatted = format(values, paramName); // { [paramName]: 'min,max' } or { [paramName]: null }
    if (queryParamNames) {
      const cleanedQuery = buildCleanedQueryWithRange(formatted);
      const params = new URLSearchParams();
      Object.entries(cleanedQuery).forEach(([k, v]) => {
        if (v != null) params.set(k, v);
      });
      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });

      // still call optional onSubmit for side-effects if consumer passed one
      onSubmit?.(formatted);
      onBlur?.();
      return;
    }

    // fallback: pass formatted to parent handler (parent responsible for merging with current query)
    onSubmit?.(formatted);
    onBlur?.();
  };

  const handleClear = () => {
    if (showTrigger) setIsOpen(false);

    if (queryParamNames) {
      const params = new URLSearchParams(location.search);
      params.delete(paramName);

      history.replace({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });

      onSubmit?.(format(null, paramName));
      onBlur?.();
      return;
    }

    onSubmit?.(format(null, paramName));
    onBlur?.();
  };

  return (
    <div className={classes} ref={dropdownRef} data-drawer={drawerKey}>
      {showTrigger ? (
        <div className={css.filterHeader}>
          <button
            type="button"
            className={classNames(css.labelButton, {
              [css.drawerVariantLabel]: variant === DRAWER_VARIANT,
              [css.labelButtonOpened]: isOpen && !variant,
            })}
            onClick={toggleIsOpen}
          >
            <span className={css.labelButtonContent}>
              {inputIcon && <span className={css.icon}>{inputIcon}</span>}
              <span className={css.labelWrapper}>
                <span
                  className={classNames(css.label, {
                    [css.labelDrawer]: variant === DRAWER_VARIANT,
                    [css.labelDrawerOpened]: isMobileDevice && isOpen,
                  })}
                >
                  {label}{' '}
                  {hasInitialValues &&
                    (showRangeInLabel ? ` (${formatRange(minVal, maxVal)})` : ' (1)')}
                </span>
              </span>

              <span
                className={classNames(css.openSign, {
                  [css.openSignLeft]: isMobileDevice && isOpen,
                })}
              >
                <IconArrowHead
                  direction={!isMobileDevice ? (isOpen ? 'up' : 'down') : isOpen ? 'left' : 'right'}
                />
              </span>
            </span>
          </button>
        </div>
      ) : (
        <div className={css.inlineLabelWrapper}>
          {inputIcon && <span className={css.icon}>{inputIcon}</span>}
          <span className={css.inlineLabel}>{label}</span>
        </div>
      )}

      {showTrigger && <div className={css.divider} />}

      <div
        className={classNames(css.formWrapper, {
          [css.formClosed]: showTrigger && !isFormVisible,
          [css.formInline]: !showTrigger,
          [css.drawerVariantForm]: variant === DRAWER_VARIANT,
        })}
      >
        <RangeFilterForm
          id={id}
          initialValues={hasInitialValues ? parsed : { min: 0 }}
          onChange={handleChange}
          onCancel={() => showTrigger && close()}
          onSubmit={handleChange}
          onClear={handleClear}
          intl={intl}
          step={step}
          unit={unit}
          isInSideBar
          presets={presets}
          {...rest}
        />
      </div>
    </div>
  );
};

export default RangeFilterPlain;
